package fr.arcplex.sooner.webui

import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.RailItem
import fr.arcplex.network.model.isSearchRail
import fr.arcplex.network.model.searchInputItem

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.SubcomposeAsyncImage
import coil3.compose.SubcomposeAsyncImageContent
import fr.arcplex.network.remoteImageUrl
import fr.arcplex.sooner.ui.theme.TVBackground
import fr.arcplex.sooner.ui.theme.TVPlay
import fr.arcplex.sooner.ui.theme.TVTxt

@Composable
fun PageView(
    page: Page,
    modifier: Modifier = Modifier,
    onRailItemClicked: ((RailItem) -> Unit)? = null,
    onMenuItemClicked: ((RailItem) -> Unit)? = null,
    onSearchQuery: ((Rail, String, Boolean) -> Unit)? = null,
    isTV: Boolean = false,
    menuOpen: Boolean = false,
    onMenuToggle: () -> Unit = {},
    onMenuDismiss: () -> Unit = {},
    coherence: Boolean = false
) {
    // Static coherence default — ignore server bgColor for now (no server call).
    val backgroundColor = TVBackground
    val hamburgerRequester = remember { FocusRequester() }
    var wasMenuOpen by remember { mutableStateOf(false) }

    // Return focus to the entry point when the drawer closes (TV/set-top box).
    LaunchedEffect(menuOpen) {
        if (wasMenuOpen && !menuOpen) runCatching { hamburgerRequester.requestFocus() }
        wasMenuOpen = menuOpen
    }

    Box(modifier = modifier.fillMaxSize().background(backgroundColor)) {
        Column(modifier = Modifier.fillMaxSize().background(backgroundColor)) {
            if (isTV) {
                TVAppBar(
                    page = page,
                    onMenuToggle = onMenuToggle,
                    hamburgerModifier = Modifier.focusRequester(hamburgerRequester)
                )
            } else if (page.menu.items.isNotEmpty()) {
                Card(
                    onClick = onMenuToggle,
                    modifier = Modifier
                        .padding(start = 8.dp, top = 8.dp)
                        .focusRequester(hamburgerRequester),
                    shape = RoundedCornerShape(0.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = Color.Transparent,
                        contentColor = TVTxt,
                        disabledContainerColor = Color.Transparent,
                        disabledContentColor = TVTxt
                    ),
                    elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
                ) {
                    Text(text = "☰", color = TVTxt, modifier = Modifier.padding(8.dp))
                }
            }

            if (isTV && page.fixed_element.items.isNotEmpty()) {
                TVHeroBanner(
                    items = page.fixed_element.items,
                    onRailItemClicked = onRailItemClicked,
                    modifier = Modifier.fillMaxWidth()
                )
            } else if (page.fixed_element.items.isNotEmpty()) {
                RailView(
                    rail = page.fixed_element,
                    modifier = Modifier,
                    centerContent = true,
                    onRailItemClicked = onRailItemClicked,
                    isTV = isTV
                )
            }

            LazyColumn(
                modifier = Modifier.weight(1f),
                contentPadding = if (isTV) PaddingValues(vertical = 8.dp) else PaddingValues(0.dp)
            ) {
                items(page.rails, key = { it.uid }) { rail ->
                    if (rail.isSearchRail) {
                        val input = rail.searchInputItem()
                        var query by remember(rail.uid) {
                            mutableStateOf(input?.content.orEmpty())
                        }
                        val onQueryChange: (String) -> Unit = { value ->
                            query = value
                            onSearchQuery?.invoke(rail, value, false)
                        }
                        val onValidate: () -> Unit = {
                            onSearchQuery?.invoke(rail, query, true)
                        }
                        if (coherence) {
                            CoherenceSearchView(
                                rail = rail,
                                query = query,
                                onQueryChange = onQueryChange,
                                onValidate = onValidate,
                                isTV = isTV
                            )
                        } else {
                            SearchView(
                                rail = rail,
                                query = query,
                                onQueryChange = onQueryChange,
                                onValidate = onValidate,
                                isTV = isTV
                            )
                        }
                    } else {
                        RailView(rail = rail, onRailItemClicked = onRailItemClicked, isTV = isTV)
                    }
                }
            }
        }

        AppMenu(
            menu = page.menu,
            isOpen = menuOpen,
            coherence = coherence,
            isTV = isTV,
            onDismiss = onMenuDismiss,
            onItemClicked = { onMenuItemClicked?.invoke(it) }
        )
    }
}

@Composable
fun TVHeroBanner(
    items: List<RailItem>,
    onRailItemClicked: ((RailItem) -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    val featuredItem = items.firstOrNull { it.type == "image" && it.content.isNotBlank() }
    val title = items.firstOrNull { it.type == "txt" }?.content ?: ""

    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(100.dp)
    ) {
        if (featuredItem != null) {
            SubcomposeAsyncImage(
                model = remoteImageUrl(featuredItem.content),
                contentDescription = null,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            ) {
                SubcomposeAsyncImageContent()
            }

            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(
                        Brush.verticalGradient(
                            0.22f to Color.Transparent,
                            0.45f to TVBackground.copy(alpha = 0.25f),
                            0.92f to TVBackground.copy(alpha = 0.96f)
                        )
                    )
            )

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(3.dp)
                    .align(Alignment.BottomCenter)
                    .background(TVPlay)
            )
        }

        if (title.isNotBlank()) {
            Text(
                text = title,
                fontSize = 28.sp,
                color = TVTxt,
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(horizontal = 32.dp, vertical = 16.dp)
            )
        }
    }
}

@Composable
fun TVAppBar(
    page: Page,
    onMenuToggle: () -> Unit,
    modifier: Modifier = Modifier,
    hamburgerModifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(56.dp)
    ) {
        var hamburgerFocused by remember { mutableStateOf(false) }

        if (page.menu.items.isNotEmpty()) {
            Card(
                onClick = onMenuToggle,
                modifier = Modifier
                    .align(Alignment.CenterStart)
                    .padding(horizontal = 16.dp)
                    .then(hamburgerModifier)
                    .onFocusChanged { hamburgerFocused = it.isFocused }
                    .then(if (hamburgerFocused) Modifier.border(3.dp, Color.White) else Modifier),
                shape = RoundedCornerShape(0.dp),
                colors = CardDefaults.cardColors(
                    containerColor = Color.Transparent,
                    contentColor = TVTxt,
                    disabledContainerColor = Color.Transparent,
                    disabledContentColor = TVTxt
                ),
                elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
            ) {
                Text(
                    text = "☰",
                    color = TVTxt,
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp)
                )
            }
        }

        if (page.label.isNotBlank()) {
            Text(
                text = page.label,
                fontSize = 18.sp,
                color = TVTxt,
                modifier = Modifier.align(Alignment.Center)
            )
        }
    }
}
